import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';

const SUPPORT_EMAIL = 'team@spirara.com';
const LAST_UPDATED = 'August 5, 2026';

const sections = [
  {
    title: '1. Acceptance of these terms',
    body: [
      'These Terms of Service (“Terms”) govern your access to and use of Spirara’s websites, apps, and related services (the “Service”). By creating an account, accessing the Service, or booking through a Spirara booking link, you agree to these Terms.',
      'If you use Spirara on behalf of a business, you confirm that you have authority to bind that business to these Terms.',
    ],
  },
  {
    title: '2. Who we are',
    body: [
      'Spirara provides booking, payments, and business management tools for salons, spas, barbershops, and related beauty and wellness businesses. The Service may include public booking pages, calendars, client records, messaging features, and payment collection via third-party processors such as Flutterwave.',
    ],
  },
  {
    title: '3. Accounts and eligibility',
    body: [
      'You must provide accurate information when you register and keep it up to date. You are responsible for safeguarding your login credentials and for activity under your account.',
      'Business owners and staff may set up locations, services, staff schedules, booking links, and payment preferences. Clients who book through a public link agree to provide accurate contact details so the business can confirm or contact them about the appointment.',
    ],
  },
  {
    title: '4. Bookings',
    body: [
      'Appointment availability, pricing, deposit rules, and cancellation or refund policies are set by each business. Spirara displays and processes bookings based on that business’s configuration.',
      'We do not guarantee that a specific time slot will remain available until a booking is successfully confirmed. Online deposit bookings may remain pending until payment is completed and verified.',
    ],
  },
  {
    title: '5. Payments and deposits',
    body: [
      'Where a business collects online deposits or payments, charges are processed by payment providers such as Flutterwave. By paying, you also agree to that provider’s terms.',
      'Spirara is not the merchant of record for every transaction; settlement to a business depends on that business’s payout setup and the payment provider’s rules. Fees charged by payment providers may apply.',
      'Refunds, chargebacks, and disputes for deposits are handled according to the business’s stated policy and the payment provider’s process. Contact the business first for appointment-related refund requests; contact us at the email below if you need help reaching support about the Spirara platform.',
    ],
  },
  {
    title: '6. Acceptable use',
    body: [
      'You agree not to misuse the Service. That includes attempting unauthorized access, disrupting the Service, scraping or harvesting data without permission, sending spam, impersonating others, or using Spirara for unlawful activity.',
      'Businesses are responsible for the accuracy of their services, hours, pricing, staff listings, and communications with clients.',
    ],
  },
  {
    title: '7. Your content and data',
    body: [
      'You retain rights to content and data you submit (such as business profile details, client notes you enter, and cover images). You grant Spirara a license to host, process, and display that content as needed to operate the Service.',
      'Our collection and use of personal information is described in our Privacy Policy.',
    ],
  },
  {
    title: '8. Intellectual property',
    body: [
      'Spirara’s name, branding, software, and site content are owned by Spirara or its licensors. You may not copy, modify, or distribute our software or branding except as allowed by these Terms or with our written permission.',
    ],
  },
  {
    title: '9. Third-party services',
    body: [
      'The Service may integrate with third parties for payments, messaging (including SMS), hosting, and analytics. Those services are governed by their own terms and privacy policies. Spirara is not responsible for third-party outages or policy changes outside our control.',
    ],
  },
  {
    title: '10. Disclaimers',
    body: [
      'The Service is provided “as is” and “as available.” To the fullest extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or free of harmful components.',
      'Spirara is a software platform. We are not a party to the service relationship between a beauty business and its clients, except to the extent we facilitate tools such as booking links and payment checkout.',
    ],
  },
  {
    title: '11. Limitation of liability',
    body: [
      'To the fullest extent permitted by law, Spirara and its officers, employees, and partners will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, data, or goodwill, arising from your use of the Service.',
      'Our total liability for any claim relating to the Service will not exceed the greater of (a) the fees you paid us for the Service in the three months before the claim or (b) USD 50 (or local currency equivalent), except where liability cannot be limited by law.',
    ],
  },
  {
    title: '12. Suspension and termination',
    body: [
      'You may stop using the Service at any time. We may suspend or terminate access if you breach these Terms, create risk for other users, fail to pay applicable fees, or if we discontinue the Service. We may also remove content that violates these Terms.',
    ],
  },
  {
    title: '13. Changes to the Service or Terms',
    body: [
      'We may update the Service and these Terms from time to time. Material changes will be posted on this page with an updated “Last updated” date. Continued use after changes become effective constitutes acceptance of the revised Terms.',
    ],
  },
  {
    title: '14. Contact',
    body: [
      `Questions about these Terms: ${SUPPORT_EMAIL}.`,
      'For privacy questions, see our Privacy Policy or email the same address.',
    ],
  },
];

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      <MarketingNav variant="light" current="/terms-of-service" />

      <main className="mx-auto max-w-3xl px-5 py-14 sm:px-6 sm:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Legal</p>
        <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-ink-600/70">Last updated: {LAST_UPDATED}</p>
        <p className="mt-4 text-base text-ink-700/80">
          Please read these Terms carefully. They explain how you may use Spirara and what you can
          expect from us.
        </p>

        <div className="mt-10 space-y-8">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="font-display text-2xl font-light text-ink-900">{section.title}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph.slice(0, 48)} className="mt-3 text-sm leading-relaxed text-ink-700/85 sm:text-base">
                  {paragraph.includes(SUPPORT_EMAIL) ? (
                    <>
                      {paragraph.split(SUPPORT_EMAIL)[0]}
                      <a
                        href={`mailto:${SUPPORT_EMAIL}`}
                        className="font-medium text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4"
                      >
                        {SUPPORT_EMAIL}
                      </a>
                      {paragraph.split(SUPPORT_EMAIL)[1] || ''}
                    </>
                  ) : (
                    paragraph
                  )}
                </p>
              ))}
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-ink-600/70">
          <Link to="/privacy-policy" className="underline decoration-ink-900/30 underline-offset-2 hover:text-ink-900">
            Privacy policy
          </Link>
          {' · '}
          <Link to="/support" className="underline decoration-ink-900/30 underline-offset-2 hover:text-ink-900">
            Support
          </Link>
        </p>
      </main>

      <footer className="border-t border-ink-900/10 bg-ink-950 py-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span className="font-display text-2xl font-light text-sand-50">
            Spirara<span className="text-saffron-500">.</span>
          </span>
          <p className="text-xs text-sand-200/50">
            &copy; {new Date().getFullYear()} Spirara. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
